import { useState, useEffect, useCallback, useMemo } from "react";
import {
  LayoutDashboard, Package, Clock, CheckCircle2, XCircle,
  Loader2, RefreshCw, Pill, Car, HeartHandshake, Stethoscope,
  UtensilsCrossed, ShoppingBasket, MapPin, ArrowLeft, LogOut,
  Mail, Lock, Building2, User, Phone, IndianRupee, Zap, AlertCircle,
  Star, TrendingUp, Wallet, Gift, Copy, FileCheck, Upload, ShieldCheck
} from "lucide-react";
import { Badge } from "@/components/ui/badge";
import axios from "axios";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SERVICE_ICONS = {
  medicine: Pill, cab: Car, nurse: HeartHandshake, doctor: Stethoscope,
  food: UtensilsCrossed, grocery: ShoppingBasket, dineout: UtensilsCrossed, ride: MapPin,
};

const SERVICE_LABELS = {
  medicine: "Medicine", cab: "Cab", nurse: "Nurse", doctor: "Doctor",
  food: "Food", grocery: "Grocery", dineout: "Dine Out", ride: "Ride",
};

const STATUS_CONFIG = {
  pending: { label: "Pending", color: "bg-yellow-100 text-yellow-800", icon: Clock },
  accepted: { label: "Accepted", color: "bg-blue-100 text-blue-800", icon: CheckCircle2 },
  in_progress: { label: "In Progress", color: "bg-purple-100 text-purple-800", icon: Loader2 },
  completed: { label: "Completed", color: "bg-green-100 text-green-800", icon: CheckCircle2 },
  cancelled: { label: "Cancelled", color: "bg-red-100 text-red-800", icon: XCircle },
};

function formatDate(iso) {
  if (!iso) return "";
  return new Date(iso).toLocaleDateString("en-IN", {
    day: "numeric", month: "short", hour: "2-digit", minute: "2-digit",
  });
}

// --- AUTH SCREEN ---
function VendorAuth({ onAuth }) {
  const [mode, setMode] = useState("login");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [name, setName] = useState("");
  const [businessName, setBusinessName] = useState("");
  const [phone, setPhone] = useState("");
  const [address, setAddress] = useState("");
  const [referralCode, setReferralCode] = useState("");
  const [categories, setCategories] = useState([]);
  const [allCategories, setAllCategories] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    axios.get(`${API}/vendor/categories`)
      .then((r) => setAllCategories(r.data.categories || []))
      .catch(() => {});
    // Pre-fill referral code from URL ?ref=CODE
    const params = new URLSearchParams(window.location.search);
    const ref = params.get("ref");
    if (ref) {
      setReferralCode(ref.toUpperCase());
      setMode("signup");
    }
  }, []);

  const toggleCategory = (id) => {
    setCategories((prev) =>
      prev.includes(id) ? prev.filter((c) => c !== id) : [...prev, id]
    );
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      if (mode === "login") {
        const res = await axios.post(`${API}/vendor/auth/login`, { email: email.trim(), password });
        if (res.data?.vendor) {
          onAuth(res.data.vendor);
          toast.success("Welcome back!");
        } else {
          setError("Unexpected response from server. Please try again.");
        }
      } else {
        if (categories.length === 0) {
          setError("Select at least one service category");
          setLoading(false);
          return;
        }
        const res = await axios.post(`${API}/vendor/auth/signup`, {
          email: email.trim(), password, name, business_name: businessName,
          service_categories: categories, phone, address,
          referral_code: referralCode.trim() || null,
        });
        if (res.data?.vendor) {
          onAuth(res.data.vendor);
          toast.success("Account created!");
        } else {
          setError("Unexpected response from server. Please try again.");
        }
      }
    } catch (err) {
      const msg = err.response?.data?.detail || err.message || "Authentication failed. Please check your credentials.";
      setError(msg);
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-4" style={{ backgroundColor: "#F5F3FF" }} data-testid="vendor-auth">
      <div className="w-full max-w-md bg-white rounded-2xl shadow-xl overflow-hidden">
        <div className="px-6 py-8 text-center" style={{ backgroundColor: "#2D2766" }}>
          <LayoutDashboard className="w-10 h-10 text-white mx-auto mb-3" />
          <h1 className="text-2xl font-bold text-white">Elderra Agent</h1>
          <p className="text-purple-200 text-sm mt-1">Vendor Portal</p>
        </div>

        <div className="flex border-b">
          {["login", "signup"].map((m) => (
            <button
              key={m}
              onClick={() => setMode(m)}
              className={`flex-1 py-3 text-sm font-medium transition-colors ${
                mode === m ? "text-[#2D2766] border-b-2 border-[#2D2766]" : "text-gray-400"
              }`}
              data-testid={`auth-tab-${m}`}
            >
              {m === "login" ? "Sign In" : "Create Account"}
            </button>
          ))}
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-4" data-testid="vendor-auth-form">
          {error && (
            <div className="bg-red-50 border border-red-200 rounded-lg px-3 py-2 text-sm text-red-700" data-testid="auth-error">
              {error}
            </div>
          )}
          {mode === "signup" && (
            <>
              <InputField icon={User} value={name} onChange={setName} placeholder="Full name" testId="vendor-name" />
              <InputField icon={Building2} value={businessName} onChange={setBusinessName} placeholder="Business name" testId="vendor-business" />
              <InputField icon={Phone} value={phone} onChange={setPhone} placeholder="Phone number" testId="vendor-phone" />
              <InputField icon={MapPin} value={address} onChange={setAddress} placeholder="Business address" testId="vendor-address" />
            </>
          )}
          <InputField icon={Mail} type="email" value={email} onChange={setEmail} placeholder="Email address" testId="vendor-email" />
          <InputField icon={Lock} type="password" value={password} onChange={setPassword} placeholder="Password" testId="vendor-password" />

          {mode === "signup" && (
            <div className="relative">
              <Gift className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
              <input
                type="text"
                value={referralCode}
                onChange={(e) => setReferralCode(e.target.value.toUpperCase())}
                placeholder="Referral code (optional)"
                className="w-full pl-10 pr-4 py-3 text-base border rounded-lg focus:outline-none focus:ring-2 focus:ring-[#2D2766] bg-gray-50"
                data-testid="vendor-referral-code"
              />
            </div>
          )}

          {mode === "signup" && allCategories.length > 0 && (
            <div data-testid="category-picker">
              <p className="text-sm font-medium text-gray-700 mb-2">Service Categories</p>
              <div className="flex flex-wrap gap-2">
                {allCategories.map((cat) => (
                  <button
                    type="button"
                    key={cat.id}
                    onClick={() => toggleCategory(cat.id)}
                    className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-all ${
                      categories.includes(cat.id)
                        ? "bg-[#2D2766] text-white border-[#2D2766]"
                        : "bg-white text-gray-600 border-gray-300 hover:border-[#2D2766]"
                    }`}
                    data-testid={`cat-${cat.id}`}
                  >
                    {cat.label}
                  </button>
                ))}
              </div>
            </div>
          )}

          <button
            type="submit"
            disabled={loading}
            className="w-full py-3 rounded-xl text-white font-semibold text-base transition-all active:scale-[0.98] disabled:opacity-50 flex items-center justify-center gap-2"
            style={{ backgroundColor: "#E8B830" }}
            data-testid="vendor-submit-btn"
          >
            {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : mode === "login" ? "Sign In" : "Create Account"}
          </button>
        </form>
      </div>
    </div>
  );
}

function InputField({ icon: Icon, type = "text", value, onChange, placeholder, testId }) {
  return (
    <div className="relative">
      <Icon className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
      <input
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        required
        className="w-full pl-10 pr-4 py-3 text-base border rounded-lg focus:outline-none focus:ring-2 focus:ring-[#2D2766] bg-gray-50"
        data-testid={testId}
      />
    </div>
  );
}

// --- MAIN DASHBOARD ---
function VendorDashboardMain({ vendor, onLogout, onRefreshVendor }) {
  const [tab, setTab] = useState("leads");
  const [leads, setLeads] = useState([]);
  const [myOrders, setMyOrders] = useState([]);
  const [earnings, setEarnings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [acceptingId, setAcceptingId] = useState(null);
  const [priceInputs, setPriceInputs] = useState({});
  const [updatingId, setUpdatingId] = useState(null);
  const [resending, setResending] = useState(false);

  const headers = {}; // auth rides on the httpOnly vendor cookie
  const isVerified = !!vendor?.is_verified;

  const resendVerification = async () => {
    setResending(true);
    try {
      await axios.post(`${API}/vendor/resend-verification`, { email: vendor.email });
      toast.success("Verification email sent! Check your inbox.");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not resend");
    } finally {
      setResending(false);
    }
  };

  const fetchLeads = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/vendor/leads`);
      setLeads(res.data.leads || []);
    } catch (e) {
      if (e.response?.status === 401) onLogout();
    }
  }, [onLogout]);

  const fetchMyOrders = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/vendor/my-orders`);
      setMyOrders(res.data.orders || []);
    } catch (e) {
      if (e.response?.status === 401) onLogout();
    }
  }, [onLogout]);

  const fetchEarnings = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/vendor/earnings`);
      setEarnings(res.data);
    } catch (e) {
      if (e.response?.status === 401) onLogout();
    }
  }, [onLogout]);

  const refresh = useCallback(async () => {
    setLoading(true);
    await Promise.all([fetchLeads(), fetchMyOrders(), fetchEarnings()]);
    setLoading(false);
  }, [fetchLeads, fetchMyOrders, fetchEarnings]);

  useEffect(() => { refresh(); }, [refresh]);

  const acceptLead = async (leadId) => {
    const price = parseFloat(priceInputs[leadId]);
    if (!price || price <= 0) {
      toast.error("Enter a valid price");
      return;
    }
    setAcceptingId(leadId);
    try {
      await axios.post(`${API}/vendor/leads/${leadId}/accept`, { price }, { headers });
      toast.success("Lead accepted! Customer will be notified.");
      setPriceInputs((prev) => { const n = {...prev}; delete n[leadId]; return n; });
      refresh();
    } catch (e) {
      const detail = e.response?.data?.detail || "Could not accept lead";
      toast.error(detail);
    } finally {
      setAcceptingId(null);
    }
  };

  const updateOrderStatus = async (orderId, newStatus) => {
    setUpdatingId(orderId);
    try {
      await axios.put(`${API}/vendor/my-orders/${orderId}/status`, { status: newStatus }, { headers });
      toast.success(`Order updated to ${newStatus}`);
      await Promise.all([fetchMyOrders(), fetchEarnings()]);
    } catch (e) {
      toast.error("Update failed");
    } finally {
      setUpdatingId(null);
    }
  };

  const pendingCount = leads.length;
  const activeCount = myOrders.filter((o) => ["accepted", "in_progress"].includes(o.status)).length;

  return (
    <div className="min-h-screen bg-gray-50" data-testid="vendor-dashboard">
      {/* Header */}
      <header className="sticky top-0 z-30 border-b bg-white shadow-sm">
        <div className="max-w-5xl mx-auto px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-lg flex items-center justify-center" style={{ backgroundColor: "#2D2766" }}>
              <LayoutDashboard className="w-5 h-5 text-white" />
            </div>
            <div>
              <h1 className="text-lg font-bold text-gray-900">{vendor.business_name}</h1>
              <p className="text-xs text-gray-500">{vendor.service_categories?.map(c => c.replace(/_/g, ' ')).join(', ')}</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={refresh} className="p-2 rounded-lg hover:bg-gray-100" data-testid="refresh-btn">
              <RefreshCw className={`w-4 h-4 text-gray-600 ${loading ? "animate-spin" : ""}`} />
            </button>
            <button onClick={onLogout} className="p-2 rounded-lg hover:bg-gray-100" data-testid="logout-btn">
              <LogOut className="w-4 h-4 text-gray-600" />
            </button>
          </div>
        </div>
      </header>

      {/* Tabs */}
      <div className="max-w-5xl mx-auto px-4 pt-4">
        {/* Email verification banner */}
        {!isVerified && (
          <div className="mb-3 rounded-xl border border-amber-300 bg-amber-50 p-3.5 flex items-start gap-3" data-testid="verify-banner">
            <AlertCircle className="w-5 h-5 text-amber-600 shrink-0 mt-0.5" />
            <div className="flex-1 text-sm">
              <p className="font-semibold text-amber-900">Verify your email to start accepting leads</p>
              <p className="text-amber-800 mt-0.5">We sent a verification link to <span className="font-mono">{vendor.email}</span>. Click the link to unlock lead acceptance.</p>
            </div>
            <button
              onClick={resendVerification}
              disabled={resending}
              className="shrink-0 text-xs font-semibold text-amber-900 underline hover:no-underline disabled:opacity-50"
              data-testid="resend-verification-btn"
            >
              {resending ? "Sending..." : "Resend email"}
            </button>
          </div>
        )}

        {/* KYC status banner */}
        {isVerified && vendor.kyc_status !== "approved" && (
          <div className={`mb-3 rounded-xl border p-3.5 flex items-start gap-3 ${
            vendor.kyc_status === "rejected" ? "border-red-300 bg-red-50"
              : vendor.kyc_status === "pending" ? "border-blue-300 bg-blue-50"
              : "border-amber-300 bg-amber-50"
          }`} data-testid="kyc-banner">
            <ShieldCheck className={`w-5 h-5 shrink-0 mt-0.5 ${
              vendor.kyc_status === "rejected" ? "text-red-600"
                : vendor.kyc_status === "pending" ? "text-blue-600"
                : "text-amber-600"
            }`} />
            <div className="flex-1 text-sm">
              {vendor.kyc_status === "pending" && (
                <>
                  <p className="font-semibold text-blue-900">KYC under review</p>
                  <p className="text-blue-800 mt-0.5">Your documents are being reviewed. You can start accepting leads once approved.</p>
                </>
              )}
              {vendor.kyc_status === "rejected" && (
                <>
                  <p className="font-semibold text-red-900">KYC rejected</p>
                  <p className="text-red-800 mt-0.5">{vendor.kyc_rejection_reason || "Please re-upload documents"}</p>
                </>
              )}
              {(!vendor.kyc_status || vendor.kyc_status === "not_submitted") && (
                <>
                  <p className="font-semibold text-amber-900">Complete KYC to unlock lead acceptance</p>
                  <p className="text-amber-800 mt-0.5">Upload your PAN card and business license in the KYC tab.</p>
                </>
              )}
            </div>
            <button
              onClick={() => setTab("kyc")}
              className="shrink-0 text-xs font-semibold underline hover:no-underline"
              data-testid="go-to-kyc-btn"
            >
              Go to KYC
            </button>
          </div>
        )}

        {/* Earnings summary */}
        {earnings && (
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2.5 mb-4" data-testid="earnings-summary">
            <div className="bg-white rounded-xl border p-3">
              <div className="flex items-center gap-1.5 text-xs text-gray-500">
                <Wallet className="w-3.5 h-3.5" /> Lifetime payout
              </div>
              <p className="text-lg font-bold text-[#2D2766] mt-1">Rs. {Number(earnings.lifetime_payout || 0).toFixed(0)}</p>
            </div>
            <div className="bg-white rounded-xl border p-3">
              <div className="flex items-center gap-1.5 text-xs text-gray-500">
                <CheckCircle2 className="w-3.5 h-3.5" /> Completed
              </div>
              <p className="text-lg font-bold text-[#2D2766] mt-1">{earnings.completed_orders || 0}</p>
            </div>
            <div className="bg-white rounded-xl border p-3">
              <div className="flex items-center gap-1.5 text-xs text-gray-500">
                <TrendingUp className="w-3.5 h-3.5" /> Active
              </div>
              <p className="text-lg font-bold text-[#2D2766] mt-1">{earnings.active_orders || 0}</p>
            </div>
            <div className="bg-white rounded-xl border p-3">
              <div className="flex items-center gap-1.5 text-xs text-gray-500">
                <Star className="w-3.5 h-3.5" /> Rating
              </div>
              <p className="text-lg font-bold text-[#2D2766] mt-1">
                {earnings.total_ratings > 0 ? `${Number(earnings.avg_rating || 0).toFixed(1)} (${earnings.total_ratings})` : "—"}
              </p>
            </div>
          </div>
        )}

        <div className="flex gap-2 mb-4 overflow-x-auto" data-testid="dashboard-tabs">
          <TabButton active={tab === "leads"} onClick={() => setTab("leads")} label="Open Leads" count={pendingCount} testId="tab-leads" />
          <TabButton active={tab === "orders"} onClick={() => setTab("orders")} label="My Orders" count={activeCount} testId="tab-orders" />
          <TabButton active={tab === "kyc"} onClick={() => setTab("kyc")} label="KYC" testId="tab-kyc" />
          <TabButton active={tab === "refer"} onClick={() => setTab("refer")} label="Refer & Earn" testId="tab-refer" />
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-4 pb-8">
        {loading ? (
          <div className="flex justify-center py-20"><Loader2 className="w-8 h-8 animate-spin text-[#2D2766]" /></div>
        ) : tab === "leads" ? (
          <LeadsList
            leads={leads}
            priceInputs={priceInputs}
            onPriceChange={(id, val) => setPriceInputs((p) => ({ ...p, [id]: val }))}
            onAccept={acceptLead}
            acceptingId={acceptingId}
          />
        ) : tab === "orders" ? (
          <OrdersList orders={myOrders} onUpdate={updateOrderStatus} updatingId={updatingId} />
        ) : tab === "kyc" ? (
          <KycPanel onRefreshVendor={onRefreshVendor} />
        ) : tab === "refer" ? (
          <ReferralPanel />
        ) : null}
      </div>
    </div>
  );
}

function TabButton({ active, onClick, label, count, testId }) {
  return (
    <button
      onClick={onClick}
      className={`flex items-center gap-2 px-4 py-2.5 rounded-lg text-sm font-medium transition-all ${
        active ? "bg-[#2D2766] text-white shadow" : "bg-white text-gray-600 border hover:bg-gray-50"
      }`}
      data-testid={testId}
    >
      {label}
      {count > 0 && (
        <span className={`text-xs px-2 py-0.5 rounded-full font-bold ${
          active ? "bg-white/20 text-white" : "bg-[#E8B830] text-white"
        }`}>{count}</span>
      )}
    </button>
  );
}

// --- LEADS LIST ---
function LeadsList({ leads, priceInputs, onPriceChange, onAccept, acceptingId }) {
  if (leads.length === 0) {
    return (
      <div className="text-center py-20 text-gray-400" data-testid="no-leads">
        <Package className="w-12 h-12 mx-auto mb-3 opacity-50" />
        <p className="text-lg font-medium">No open leads right now</p>
        <p className="text-sm mt-1">New orders will appear here automatically</p>
      </div>
    );
  }

  return (
    <div className="space-y-3" data-testid="leads-list">
      {leads.map((lead) => {
        const Icon = SERVICE_ICONS[lead.service_type] || Package;
        return (
          <div key={lead.id} className="bg-white rounded-xl border shadow-sm p-4" data-testid={`lead-${lead.id}`}>
            <div className="flex items-start justify-between mb-3">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-purple-50 flex items-center justify-center">
                  <Icon className="w-5 h-5 text-[#2D2766]" />
                </div>
                <div>
                  <p className="font-semibold text-gray-900">{SERVICE_LABELS[lead.service_type] || lead.service_type}</p>
                  <p className="text-xs text-gray-400">{formatDate(lead.created_at)}</p>
                </div>
              </div>
              <Badge className="bg-yellow-100 text-yellow-800 border-yellow-200">New Lead</Badge>
            </div>

            {/* Order details */}
            <div className="bg-gray-50 rounded-lg p-3 mb-3 text-sm">
              {lead.details && Object.entries(lead.details).map(([k, v]) => (
                <div key={k} className="flex justify-between py-0.5">
                  <span className="text-gray-500 capitalize">{k.replace(/_/g, ' ')}</span>
                  <span className="text-gray-800 font-medium">{typeof v === 'object' ? JSON.stringify(v) : String(v)}</span>
                </div>
              ))}
              {lead.address && (
                <div className="flex justify-between py-0.5">
                  <span className="text-gray-500">Address</span>
                  <span className="text-gray-800 font-medium text-right max-w-[60%]">{lead.address}</span>
                </div>
              )}
            </div>

            {/* Accept with price */}
            <div className="flex items-center gap-2" data-testid={`accept-form-${lead.id}`}>
              <div className="relative flex-1">
                <IndianRupee className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                <input
                  type="number"
                  placeholder="Your price"
                  value={priceInputs[lead.id] || ""}
                  onChange={(e) => onPriceChange(lead.id, e.target.value)}
                  className="w-full pl-9 pr-4 py-2.5 text-base border rounded-lg focus:outline-none focus:ring-2 focus:ring-[#2D2766]"
                  data-testid={`price-input-${lead.id}`}
                />
              </div>
              <button
                onClick={() => onAccept(lead.id)}
                disabled={acceptingId === lead.id}
                className="flex items-center gap-2 px-5 py-2.5 rounded-lg text-white font-medium text-sm transition-all active:scale-95 disabled:opacity-50"
                style={{ backgroundColor: "#E8B830" }}
                data-testid={`accept-btn-${lead.id}`}
              >
                {acceptingId === lead.id ? <Loader2 className="w-4 h-4 animate-spin" /> : <Zap className="w-4 h-4" />}
                Accept
              </button>
            </div>
          </div>
        );
      })}
    </div>
  );
}

// --- ORDERS LIST ---
function OrdersList({ orders, onUpdate, updatingId }) {
  if (orders.length === 0) {
    return (
      <div className="text-center py-20 text-gray-400" data-testid="no-orders">
        <CheckCircle2 className="w-12 h-12 mx-auto mb-3 opacity-50" />
        <p className="text-lg font-medium">No orders yet</p>
        <p className="text-sm mt-1">Accept a lead to start fulfilling orders</p>
      </div>
    );
  }

  const STATUS_ACTIONS = {
    accepted: [{ next: "in_progress", label: "Start" }, { next: "cancelled", label: "Cancel" }],
    in_progress: [{ next: "completed", label: "Complete" }, { next: "cancelled", label: "Cancel" }],
  };

  return (
    <div className="space-y-3" data-testid="orders-list">
      {orders.map((order) => {
        const Icon = SERVICE_ICONS[order.service_type] || Package;
        const statusCfg = STATUS_CONFIG[order.status] || STATUS_CONFIG.pending;
        const actions = STATUS_ACTIONS[order.status] || [];
        return (
          <div key={order.id} className="bg-white rounded-xl border shadow-sm p-4" data-testid={`order-${order.id}`}>
            <div className="flex items-start justify-between mb-3">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-purple-50 flex items-center justify-center">
                  <Icon className="w-5 h-5 text-[#2D2766]" />
                </div>
                <div>
                  <p className="font-semibold text-gray-900">{SERVICE_LABELS[order.service_type] || order.service_type}</p>
                  <p className="text-xs text-gray-400">{formatDate(order.accepted_at || order.created_at)}</p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                {order.vendor_price && (
                  <div className="text-right">
                    <span className="text-sm font-bold text-[#2D2766]">Rs. {order.vendor_price}</span>
                    {order.vendor_payout && (
                      <p className="text-[10px] text-gray-500 leading-tight">
                        Payout Rs. {Number(order.vendor_payout).toFixed(0)}
                        {order.commission_rate != null && <> · {order.commission_rate}% fee</>}
                      </p>
                    )}
                  </div>
                )}
                <Badge className={statusCfg.color}>{statusCfg.label}</Badge>
              </div>
            </div>

            <div className="bg-gray-50 rounded-lg p-3 mb-3 text-sm">
              {order.details && Object.entries(order.details).map(([k, v]) => (
                <div key={k} className="flex justify-between py-0.5">
                  <span className="text-gray-500 capitalize">{k.replace(/_/g, ' ')}</span>
                  <span className="text-gray-800 font-medium">{typeof v === 'object' ? JSON.stringify(v) : String(v)}</span>
                </div>
              ))}
              {order.address && (
                <div className="flex justify-between py-0.5">
                  <span className="text-gray-500">Address</span>
                  <span className="text-gray-800 font-medium text-right max-w-[60%]">{order.address}</span>
                </div>
              )}
            </div>

            {actions.length > 0 && (
              <div className="flex gap-2">
                {actions.map((action) => (
                  <button
                    key={action.next}
                    onClick={() => onUpdate(order.id, action.next)}
                    disabled={updatingId === order.id}
                    className={`flex-1 py-2 rounded-lg text-sm font-medium transition-all ${
                      action.next === "cancelled"
                        ? "bg-red-50 text-red-600 hover:bg-red-100"
                        : "text-white hover:opacity-90"
                    }`}
                    style={action.next !== "cancelled" ? { backgroundColor: "#2D2766" } : {}}
                    data-testid={`action-${action.next}-${order.id}`}
                  >
                    {updatingId === order.id ? <Loader2 className="w-4 h-4 animate-spin mx-auto" /> : action.label}
                  </button>
                ))}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

// --- KYC PANEL ---
function KycPanel({ onRefreshVendor }) {
  const [kyc, setKyc] = useState(null);
  const [loading, setLoading] = useState(true);
  const [uploadingType, setUploadingType] = useState(null);
  // Auth rides on the httpOnly vendor cookie (axios withCredentials).
  const headers = useMemo(() => ({}), []);

  const fetchKyc = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/vendor/kyc`, { headers });
      setKyc(res.data);
    } catch (e) {
      toast.error("Could not load KYC status");
    } finally {
      setLoading(false);
    }
  }, [headers]);

  useEffect(() => { fetchKyc(); }, [fetchKyc]);

  const upload = async (docType, file) => {
    if (!file) return;
    if (file.size > 10 * 1024 * 1024) {
      toast.error("File must be under 10 MB");
      return;
    }
    setUploadingType(docType);
    try {
      const form = new FormData();
      form.append("doc_type", docType);
      form.append("file", file);
      await axios.post(`${API}/vendor/kyc/upload`, form, { headers });
      toast.success(`${docType.toUpperCase()} uploaded`);
      await fetchKyc();
      if (onRefreshVendor) await onRefreshVendor();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Upload failed");
    } finally {
      setUploadingType(null);
    }
  };

  if (loading) return <div className="flex justify-center py-16"><Loader2 className="w-6 h-6 animate-spin text-[#2D2766]" /></div>;

  const DOC_META = {
    pan: { label: "PAN Card", required: true, desc: "Upload a clear image/PDF of your PAN card" },
    business_license: { label: "Business License / Registration", required: true, desc: "GST cert, shops & establishment, or business registration proof" },
    gst: { label: "GST Certificate", required: false, desc: "Optional - if GST registered" },
    aadhaar: { label: "Aadhaar (Proprietor)", required: false, desc: "Optional - for individual proprietors" },
  };

  const docsByType = {};
  (kyc?.documents || []).forEach(d => { docsByType[d.type] = d; });

  const statusBadge = () => {
    const s = kyc?.kyc_status || "not_submitted";
    const map = {
      not_submitted: { label: "Not Submitted", cls: "bg-gray-100 text-gray-700" },
      pending: { label: "Under Review", cls: "bg-blue-100 text-blue-700" },
      approved: { label: "Approved", cls: "bg-green-100 text-green-700" },
      rejected: { label: "Rejected", cls: "bg-red-100 text-red-700" },
    };
    const m = map[s];
    return <span className={`text-xs font-semibold px-2.5 py-1 rounded-full ${m.cls}`} data-testid="kyc-status-badge">{m.label}</span>;
  };

  return (
    <div className="space-y-4" data-testid="kyc-panel">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-bold text-gray-900">KYC Documents</h3>
          <p className="text-sm text-gray-500">Upload your business documents to get verified and start accepting leads.</p>
        </div>
        {statusBadge()}
      </div>

      {kyc?.kyc_status === "rejected" && kyc?.rejection_reason && (
        <div className="rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-800">
          <p className="font-semibold">Rejection reason:</p>
          <p>{kyc.rejection_reason}</p>
        </div>
      )}

      <div className="space-y-3">
        {Object.entries(DOC_META).map(([type, meta]) => {
          const existing = docsByType[type];
          return (
            <div key={type} className="bg-white border rounded-xl p-4" data-testid={`kyc-doc-${type}`}>
              <div className="flex items-start justify-between gap-3">
                <div className="flex-1">
                  <div className="flex items-center gap-2">
                    <FileCheck className="w-4 h-4 text-[#2D2766]" />
                    <p className="font-semibold text-sm text-gray-900">{meta.label}</p>
                    {meta.required && <span className="text-[10px] font-bold text-red-500">REQUIRED</span>}
                  </div>
                  <p className="text-xs text-gray-500 mt-1">{meta.desc}</p>
                  {existing && (
                    <div className="mt-2 flex items-center gap-2 text-xs text-green-700">
                      <CheckCircle2 className="w-3.5 h-3.5" />
                      <span className="truncate max-w-[220px]">{existing.original_filename}</span>
                      <span className="text-gray-400">· {formatDate(existing.uploaded_at)}</span>
                    </div>
                  )}
                </div>
                <label
                  className="shrink-0 cursor-pointer px-3 py-2 rounded-lg text-xs font-semibold border border-[#2D2766] text-[#2D2766] hover:bg-[#2D2766] hover:text-white transition-colors flex items-center gap-1.5"
                  data-testid={`kyc-upload-${type}`}
                >
                  {uploadingType === type ? (
                    <Loader2 className="w-3.5 h-3.5 animate-spin" />
                  ) : (
                    <Upload className="w-3.5 h-3.5" />
                  )}
                  {existing ? "Replace" : "Upload"}
                  <input
                    type="file"
                    className="hidden"
                    accept="image/jpeg,image/png,image/webp,application/pdf"
                    onChange={(e) => upload(type, e.target.files?.[0])}
                    disabled={uploadingType !== null}
                  />
                </label>
              </div>
            </div>
          );
        })}
      </div>
      <p className="text-xs text-gray-500 text-center">Accepted: JPG, PNG, WebP, PDF · Max 10 MB</p>
    </div>
  );
}

// --- REFERRAL PANEL ---
function ReferralPanel() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    axios.get(`${API}/vendor/referrals`)
      .then((r) => setData(r.data))
      .catch(() => toast.error("Could not load referrals"))
      .finally(() => setLoading(false));
  }, []);

  if (loading) return <div className="flex justify-center py-16"><Loader2 className="w-6 h-6 animate-spin text-[#2D2766]" /></div>;
  if (!data) return null;

  const referralLink = `${window.location.origin}/vendor?ref=${data.referral_code}`;
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(referralLink);
      setCopied(true);
      toast.success("Referral link copied!");
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.error("Could not copy");
    }
  };

  return (
    <div className="space-y-4" data-testid="referral-panel">
      <div>
        <h3 className="text-lg font-bold text-gray-900">Refer & Earn</h3>
        <p className="text-sm text-gray-500">
          Invite other businesses. When a vendor you refer completes {data.trigger_orders} orders,
          both of you get <span className="font-semibold text-[#2D2766]">{data.reward_percent}% off</span> your
          commission for {data.reward_days} days.
        </p>
      </div>

      {/* Code + link */}
      <div className="bg-gradient-to-br from-[#2D2766] to-[#453c8a] rounded-2xl p-5 text-white">
        <p className="text-xs uppercase tracking-wider text-white/70">Your referral code</p>
        <p className="text-3xl font-bold mt-1 font-mono" data-testid="referral-code">{data.referral_code}</p>
        <div className="mt-4 flex items-stretch gap-2">
          <input
            readOnly
            value={referralLink}
            className="flex-1 px-3 py-2 rounded-lg bg-white/10 text-white text-xs font-mono border border-white/20 focus:outline-none"
            data-testid="referral-link"
          />
          <button
            onClick={copy}
            className="px-3 py-2 rounded-lg bg-[#E8B830] text-[#2D2766] text-xs font-bold flex items-center gap-1.5"
            data-testid="copy-referral-btn"
          >
            <Copy className="w-3.5 h-3.5" /> {copied ? "Copied!" : "Copy"}
          </button>
        </div>
      </div>

      {/* Status cards */}
      <div className="grid grid-cols-3 gap-2.5">
        <div className="bg-white rounded-xl border p-3">
          <p className="text-xs text-gray-500">Referred</p>
          <p className="text-xl font-bold text-[#2D2766] mt-1">{data.total_referred}</p>
        </div>
        <div className="bg-white rounded-xl border p-3">
          <p className="text-xs text-gray-500">Discount</p>
          <p className="text-xl font-bold text-[#2D2766] mt-1">
            {data.active_discount ? `${data.discount_percent}%` : "—"}
          </p>
        </div>
        <div className="bg-white rounded-xl border p-3">
          <p className="text-xs text-gray-500">Days left</p>
          <p className="text-xl font-bold text-[#2D2766] mt-1">
            {data.active_discount ? data.discount_days_left : "—"}
          </p>
        </div>
      </div>

      {/* Referrals list */}
      {data.referrals.length > 0 ? (
        <div className="bg-white border rounded-xl divide-y">
          {data.referrals.map((r) => (
            <div key={r.id} className="p-3 flex items-center justify-between" data-testid={`referral-${r.id}`}>
              <div>
                <p className="font-semibold text-sm text-gray-900">{r.business_name}</p>
                <p className="text-xs text-gray-500">{r.name} · joined {formatDate(r.created_at)}</p>
              </div>
              <div className="text-right">
                <p className="text-xs font-semibold text-gray-700">{r.completed_orders || 0} orders</p>
                <p className="text-[10px] text-gray-400">KYC: {r.kyc_status || "not_submitted"}</p>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="text-center py-12 text-gray-400 bg-white border rounded-xl">
          <Gift className="w-8 h-8 mx-auto mb-2 opacity-50" />
          <p className="text-sm">Share your code to invite partners.</p>
        </div>
      )}
    </div>
  );
}

// --- MAIN EXPORT ---
export default function VendorDashboard({ onBack }) {
  const [vendor, setVendor] = useState(null);
  const [checking, setChecking] = useState(true);

  // The vendor token is an httpOnly cookie — ask the server who we are.
  useEffect(() => {
    axios.get(`${API}/vendor/auth/me`)
      .then((res) => setVendor(res.data))
      .catch(() => setVendor(null))
      .finally(() => setChecking(false));
  }, []);

  const handleAuth = useCallback((vendorData) => {
    setVendor(vendorData);
  }, []);

  const handleLogout = useCallback(async () => {
    try {
      await axios.post(`${API}/vendor/auth/logout`);
    } catch (_) { /* cookie may already be gone */ }
    setVendor(null);
  }, []);

  const refreshVendor = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/vendor/auth/me`);
      setVendor(res.data);
    } catch (e) {
      console.error("Failed to refresh vendor profile:", e);
    }
  }, []);

  if (checking) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <Loader2 className="w-8 h-8 animate-spin text-[#2D2766]" />
      </div>
    );
  }

  if (!vendor) {
    return <VendorAuth onAuth={handleAuth} />;
  }

  return <VendorDashboardMain vendor={vendor} onLogout={handleLogout} onRefreshVendor={refreshVendor} />;
}
